import React, { useState, useEffect } from 'react';
import './CertificationTracker.css';
import CAMSCourse from './CAMSCourse';

export function CertificationTracker({ userId, userEmail }) {
  const [certifications, setcertifications] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCert, setSelectedCert] = useState(null);
  const [error, setError] = useState(null);
  const [showCAMSCourse, setShowCAMSCourse] = useState(false);

  useEffect(() => {
    loadCertifications();
    loadProgress();
  }, [userId]);

  const loadCertifications = async () => {
    try {
      const response = await fetch('/api/training/certifications');
      if (!response.ok) throw new Error('Failed to load certifications');
      const data = await response.json();
      setcertifications(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await fetch(`/api/training/certification-progress?userId=${userId}`);
      if (!response.ok) throw new Error('Failed to load progress');
      const data = await response.json();
      const progressMap = {};
      data.forEach(item => {
        progressMap[item.certification_id] = item;
      });
      setProgress(progressMap);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const updateProgress = async (certId, newStatus, newProgress = 0, examPassed = false) => {
    try {
      const response = await fetch('/api/training/certification-progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          certificationId: certId,
          status: newStatus,
          progressPercentage: newProgress,
          examPassed
        })
      });

      if (!response.ok) throw new Error('Failed to update progress');
      const updated = await response.json();
      setProgress({
        ...progress,
        [certId]: updated
      });
    } catch (err) {
      console.error('Error updating progress:', err);
    }
  };

  if (loading) {
    return <div className="cert-loading">Loading certifications...</div>;
  }

  // If CAMS course is selected, show the CAMS course component
  if (showCAMSCourse) {
    return (
      <div className="certification-tracker">
        <div className="cert-header">
          <button
            onClick={() => setShowCAMSCourse(false)}
            className="back-to-certs-btn"
          >
            ← Back to Certifications
          </button>
        </div>
        <CAMSCourse
          userId={userId}
          userEmail={userEmail}
          onComplete={() => setShowCAMSCourse(false)}
        />
      </div>
    );
  }

  if (error) {
    return <div className="cert-error">Error: {error}</div>;
  }

  return (
    <div className="certification-tracker">
      <div className="cert-header">
        <h1>🎯 Professional Certifications</h1>
        <p>Build your professional credentials with industry-recognized certifications</p>
      </div>

      <div className="cert-grid">
        {/* CAMS CERTIFICATION COURSE - FEATURED */}
        <div
          className="cert-card cams-featured"
          onClick={() => setShowCAMSCourse(true)}
        >
          <div className="cert-icon">🎖️</div>
          <div className="badge-new">NEW</div>
          <h2>CAMS Certification Exam Preparation</h2>
          <p className="cert-provider">CrossReg Nexus Consultants</p>

          <div className="cert-description-short">
            Six study modules covering the CAMS exam domains, each with a knowledge check, plus a final practice exam
          </div>

          <div className="cert-highlights">
            <div className="highlight">📚 6 Modules</div>
            <div className="highlight">📝 Final exam</div>
            <div className="highlight">✅ Quizzes</div>
            <div className="highlight">🎖️ Certificate</div>
          </div>

          <button className="cert-button cams-start">
            🚀 Start CAMS Course
          </button>
        </div>

        {/* OTHER CERTIFICATIONS */}
        {certifications.map((cert) => {
          const certProgress = progress[cert.id] || { status: 'not-started', progress_percentage: 0 };
          const isSelected = selectedCert === cert.id;

          return (
            <div
              key={cert.id}
              className={`cert-card ${certProgress.status} ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedCert(isSelected ? null : cert.id)}
            >
              <div className="cert-icon">
                {certProgress.status === 'certified' ? '✓' : '📜'}
              </div>

              <h2>{cert.name}</h2>
              <p className="cert-provider">{cert.exam_provider}</p>

              <div className="cert-progress-bar">
                <div
                  className="cert-progress-fill"
                  style={{ width: `${certProgress.progress_percentage}%` }}
                ></div>
              </div>

              <div className="cert-status">
                {certProgress.status === 'not-started' && 'Not Started'}
                {certProgress.status === 'in-progress' && `${certProgress.progress_percentage}% Complete`}
                {certProgress.status === 'exam-eligible' && 'Ready for Exam'}
                {certProgress.status === 'certified' && 'Certified! 🎉'}
              </div>

              {isSelected && (
                <div className="cert-details" onClick={(e) => e.stopPropagation()}>
                  <p className="cert-description">{cert.description}</p>

                  {cert.prerequisites && (
                    <div className="cert-prerequisites">
                      <h3>Prerequisites</h3>
                      <p>{cert.prerequisites}</p>
                    </div>
                  )}

                  <div className="cert-actions">
                    {certProgress.status === 'not-started' && (
                      <button
                        onClick={() => updateProgress(cert.id, 'in-progress', 25)}
                        className="cert-button start"
                      >
                        Start Preparation
                      </button>
                    )}

                    {certProgress.status === 'in-progress' && (
                      <div className="cert-action-group">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={certProgress.progress_percentage}
                          onChange={(e) => updateProgress(cert.id, 'in-progress', parseInt(e.target.value))}
                          className="cert-slider"
                        />
                        <p className="slider-label">{certProgress.progress_percentage}% prepared</p>
                        <button
                          onClick={() => updateProgress(cert.id, 'exam-eligible', 100)}
                          className="cert-button eligible"
                        >
                          Ready for Exam
                        </button>
                      </div>
                    )}

                    {certProgress.status === 'exam-eligible' && (
                      <div className="cert-action-group">
                        <p className="exam-message">Take the exam and record your result</p>
                        <button
                          onClick={() => updateProgress(cert.id, 'certified', 100, true)}
                          className="cert-button certified"
                        >
                          Exam Passed!
                        </button>
                        <button
                          onClick={() => updateProgress(cert.id, 'in-progress', 80)}
                          className="cert-button retry"
                        >
                          Need More Prep
                        </button>
                      </div>
                    )}

                    {certProgress.status === 'certified' && (
                      <div className="cert-certified-badge">
                        <p>✓ Marked as certified on {new Date(certProgress.updated_at).toLocaleDateString()}</p>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Self-reported. Keep the certificate issued by {cert.exam_provider} for your records.</p>
                        <button
                          onClick={() => updateProgress(cert.id, 'in-progress', 80)}
                          className="cert-button retry"
                        >
                          Undo
                        </button>
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>
          );
        })}
      </div>

      {certifications.length === 0 && (
        <div className="cert-empty">
          <p>More certifications coming soon. Start with CAMS!</p>
        </div>
      )}
    </div>
  );
}

export default CertificationTracker;
